import AddIcon from "@mui/icons-material/Add";
import PersonIcon from "@mui/icons-material/Person";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Toolbar } from "../../components/ui/toolbar/toolbar.component";
import { Action } from "../../types/toolbar";
import CustomModal from "../../components/ui/modal/modal.component";
import { useCallback, useEffect, useMemo, useState } from "react";
import JobForm from "../../components/service/jobForm.component";
import { useAuthContext, useHasAnyPermission } from "../../contexts/auth";
import { JOB_STATUS_OPTIONS } from "../../consts";
import { serviceActionsSectionStyle } from "../../styles/commonStyles";
import { useAnalytics } from "../../api/analytics";
import { JobsByStatus } from "../../types/analytics";

const QuickFilterCountAdornment = ({ count }: { count: number | null }) => (
  <Box
    sx={{
      width: 26,
      height: 20,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 12,
      fontWeight: 700,
      color: "text.primary",
      flexShrink: 0,
    }}
  >
    {count === null ? "..." : count}
  </Box>
);

export const JobsListActions = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [addJobOpened, setAddJobOpened] = useState<boolean>(false);
  const canAddJob = useHasAnyPermission(["service.add_job", "service.add_internal_job"]);
  const { currentUser } = useAuthContext();
  const { getAnalytics } = useAnalytics();
  const [jobsByStatus, setJobsByStatus] = useState<JobsByStatus | null>(null);
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const statusParam = params.get("status") || "";
  const assignedToParam = params.get("assigned_to") || "";
  const currentUserId = currentUser ? String(currentUser.id) : "";
  const isAssignedToMe = !!currentUserId && assignedToParam === currentUserId;

  const fetchJobCounts = useCallback(async () => {
    const data = await getAnalytics();
    if (data) {
      setJobsByStatus(data.jobs_by_status);
    }
  }, [getAnalytics]);

  useEffect(() => {
    fetchJobCounts();
  }, [fetchJobCounts]);

  const totalJobsCount = jobsByStatus
    ? Object.values(jobsByStatus).reduce((sum, count) => sum + (count || 0), 0)
    : null;

  const updateStatusParam = (status?: string) => {
    const nextParams = new URLSearchParams(location.search);

    if (status) {
      nextParams.set("status", status);
    } else {
      nextParams.delete("status");
    }

    const nextSearch = nextParams.toString();
    navigate({
      pathname: location.pathname,
      search: nextSearch ? `?${nextSearch}` : "",
    });
  };

  const toggleAssignedToMe = () => {
    if (!currentUserId) {
      return;
    }

    const nextParams = new URLSearchParams(location.search);

    if (isAssignedToMe) {
      nextParams.delete("assigned_to");
    } else {
      nextParams.set("assigned_to", currentUserId);
    }

    const nextSearch = nextParams.toString();
    navigate({
      pathname: location.pathname,
      search: nextSearch ? `?${nextSearch}` : "",
    });
  };

  const quickFilterActions: Action[] = [
    {
      icon: <PersonIcon />,
      label: t("Assigned to me"),
      onClick: toggleAssignedToMe,
      color: isAssignedToMe ? "var(--primaryColor)" : undefined,
      disabled: !currentUser,
    },
    {
      icon: <QuickFilterCountAdornment count={totalJobsCount} />,
      label: t("All statuses"),
      onClick: () => updateStatusParam(undefined),
      color: !statusParam ? "var(--primaryColor)" : undefined,
    },
    ...JOB_STATUS_OPTIONS.filter((option) => {
      if (!jobsByStatus) {
        return false;
      }

      return (jobsByStatus[option.value as keyof JobsByStatus] ?? 0) > 0;
    }).map((option) => {
      const isActive = statusParam === option.value;

      return {
        icon: (
          <QuickFilterCountAdornment
            count={jobsByStatus ? jobsByStatus[option.value as keyof JobsByStatus] ?? 0 : null}
          />
        ),
        label: t(option.labelKey),
        onClick: () => updateStatusParam(option.value),
        color: isActive ? "var(--primaryColor)" : undefined,
      };
    }),
  ];

  const quickFilters = (
    <Box component="fieldset" sx={serviceActionsSectionStyle}>
      <Typography
        component="legend"
        variant="subtitle2"
        sx={{
          px: 1,
          mb: 1,
        }}
      >
        {t("Quick filters")}
      </Typography>
      <Toolbar actions={quickFilterActions} noTopPadding showShortcut={false} />
    </Box>
  );

  const actions: Action[] = [{
    id: "new-job",
    icon: <AddIcon />,
    label: t("New job"),
    onClick: () => { setAddJobOpened(true) },
    disabled: !canAddJob
  }];

  const handleJobAdded = () => {
    setAddJobOpened(false);
    fetchJobCounts();
    window.dispatchEvent(new Event('jobAdded'));
  };

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", width: "100%", height: "100%", minHeight: 0 }}>
        {quickFilters}
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <Toolbar actions={actions} noTopPadding />
        </Box>
      </Box>
      <CustomModal
        opened={addJobOpened}
        setOpened={setAddJobOpened}
        title={t("New job")}
        content={
          <JobForm onSuccess={handleJobAdded} />
        }
      />
    </>
  );
};
